/**
 * 支払い方法関連のユーティリティ関数
 * 複数の場所で使用されているクレジットカード判定ロジックを統一化
 * 対応支払い方法: クレジットカード、口座振替のみ
 */

/**
 * 支払い方法の定数定義
 */
export const PAYMENT_METHODS = {
  CREDIT_CARD: 'CREDIT_CARD',
  BANK_TRANSFER: 'BANK_TRANSFER',
  WEB_BANK_TRANSFER: 'WEB_BANK_TRANSFER',
  MANUAL_TRANSFER: 'MANUAL_TRANSFER',
  CASH: 'CASH'
};

/**
 * クレジットカードに該当するキーの配列
 */
const CREDIT_CARD_KEYS = [
  'CREDIT_CARD',
  'creditCard',
  'credit_card',
  'クレジットカード決済',
  'クレジットカード',
  'creditCardPayment'
];

/**
 * 口座振替に該当するキーの配列
 */
const BANK_TRANSFER_KEYS = [
  'BANK_TRANSFER',
  'bankTransfer',
  'AUTO_DEBIT',
  '口座振替',
  '銀行振込'
];

/**
 * WEB口振に該当するキーの配列
 */
const WEB_BANK_TRANSFER_KEYS = [
  'WEB_BANK_TRANSFER',
  'webBankTransfer',
  'WEB口振'
];

/**
 * 振込に該当するキーの配列
 */
const MANUAL_TRANSFER_KEYS = [
  'MANUAL_TRANSFER',
  'manualTransfer',
  '振込'
];

/**
 * 店頭に該当するキーの配列
 */
const CASH_KEYS = [
  'CASH',
  'cash',
  '店頭',
  '現金'
];

/**
 * 支払い方法がクレジットカードかどうかを判定する統一関数
 * @param {string|null|undefined} paymentMethod - 支払い方法
 * @returns {boolean} クレジットカードの場合はtrue
 */
export const isCreditCardPayment = (paymentMethod) => {
  if (!paymentMethod) return false;
  
  return CREDIT_CARD_KEYS.includes(paymentMethod);
};

/**
 * 支払い方法が口座振替かどうかを判定する関数
 * @param {string|null|undefined} paymentMethod - 支払い方法
 * @returns {boolean} 口座振替の場合はtrue
 */
export const isBankTransferPayment = (paymentMethod) => {
  if (!paymentMethod) return false;
  
  return BANK_TRANSFER_KEYS.includes(paymentMethod);
};

/**
 * 支払い方法がWEB口振かどうかを判定する関数
 */
export const isWebBankTransferPayment = (paymentMethod) => {
  if (!paymentMethod) return false;
  return WEB_BANK_TRANSFER_KEYS.includes(paymentMethod);
};

/**
 * 支払い方法が振込かどうかを判定する関数
 */
export const isManualTransferPayment = (paymentMethod) => {
  if (!paymentMethod) return false;
  return MANUAL_TRANSFER_KEYS.includes(paymentMethod);
};

/**
 * 支払い方法が店頭かどうかを判定する関数
 */
export const isCashPayment = (paymentMethod) => {
  if (!paymentMethod) return false;
  return CASH_KEYS.includes(paymentMethod);
};

/**
 * 支払い方法をAPI形式（英語定数）に変換
 * @param {string} paymentMethod - フォームや表示用の値
 * @returns {string} API用の値
 */
export const convertToApiFormat = (paymentMethod) => {
  if (!paymentMethod) return PAYMENT_METHODS.CREDIT_CARD;
  
  if (isCreditCardPayment(paymentMethod)) {
    return PAYMENT_METHODS.CREDIT_CARD;
  }
  
  if (isBankTransferPayment(paymentMethod)) {
    return PAYMENT_METHODS.BANK_TRANSFER;
  }

  if (isWebBankTransferPayment(paymentMethod)) {
    return PAYMENT_METHODS.WEB_BANK_TRANSFER;
  }

  if (isManualTransferPayment(paymentMethod)) {
    return PAYMENT_METHODS.MANUAL_TRANSFER;
  }

  if (isCashPayment(paymentMethod)) {
    return PAYMENT_METHODS.CASH;
  }
  
  // フォールバック: デフォルトはクレジットカード
  return PAYMENT_METHODS.CREDIT_CARD;
};

/**
 * 支払い方法を日本語表示名に変換
 * @param {string} paymentMethod - API形式や内部形式の値
 * @returns {string} 日本語表示名
 */
export const convertToDisplayName = (paymentMethod) => {
  if (!paymentMethod) return '';
  
  if (isCreditCardPayment(paymentMethod)) {
    return 'クレジットカード';
  }
  
  if (isBankTransferPayment(paymentMethod)) {
    return '口座振替';
  }
  
  if (isWebBankTransferPayment(paymentMethod)) {
    return 'WEB口振';
  }
  if (isManualTransferPayment(paymentMethod)) {
    return '振込';
  }
  if (isCashPayment(paymentMethod)) {
    return '店頭';
  }

  return paymentMethod;
};

/**
 * 支払い方法の正規化（統一フォーマットに変換）
 * @param {string} paymentMethod - 任意の形式の支払い方法
 * @returns {string} 正規化された支払い方法（API形式）
 */
export const normalizePaymentMethod = (paymentMethod) => {
  return convertToApiFormat(paymentMethod);
};

/**
 * 利用可能な支払い方法の一覧を取得
 * @param {boolean} includeDisplay - 表示名も含めるかどうか
 * @returns {Array} 支払い方法の配列
 */
export const getAvailablePaymentMethods = (includeDisplay = false) => {
  const methods = Object.values(PAYMENT_METHODS);
  
  if (!includeDisplay) {
    return methods;
  }
  
  return methods.map(method => ({
    value: method,
    label: convertToDisplayName(method)
  }));
};

/**
 * 支払い方法の検証
 * @param {string} paymentMethod - 検証する支払い方法
 * @returns {boolean} 有効な支払い方法の場合はtrue
 */
export const isValidPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) return false;
  
  return isCreditCardPayment(paymentMethod) ||
         isBankTransferPayment(paymentMethod) ||
         isWebBankTransferPayment(paymentMethod) ||
         isManualTransferPayment(paymentMethod) ||
         isCashPayment(paymentMethod);
};

/**
 * デフォルトの支払い方法を取得
 * @returns {string} デフォルトの支払い方法
 */
export const getDefaultPaymentMethod = () => {
  return PAYMENT_METHODS.CREDIT_CARD;
};

const paymentMethodUtils = {
  PAYMENT_METHODS,
  isCreditCardPayment,
  isBankTransferPayment,
  isWebBankTransferPayment,
  isManualTransferPayment,
  isCashPayment,
  convertToApiFormat,
  convertToDisplayName,
  normalizePaymentMethod,
  getAvailablePaymentMethods,
  isValidPaymentMethod,
  getDefaultPaymentMethod
};

export default paymentMethodUtils;